import React, { useEffect, useState } from 'react';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '../ui/sheet';
import { ordersAPI } from '../../services/api';
import {
  Loader2, CheckCircle2, AlertTriangle, XCircle, CreditCard,
  ShieldCheck, UserCog, FileText, RefreshCw, History,
} from 'lucide-react';

const KIND = {
  created: { Icon: FileText, dot: 'bg-slate-400', text: 'text-slate-800' },
  charge: { Icon: CreditCard, dot: 'bg-slate-500', text: 'text-slate-800' },
  payment: { Icon: ShieldCheck, dot: 'bg-emerald-500', text: 'text-emerald-700' },
  credit: { Icon: RefreshCw, dot: 'bg-amber-500', text: 'text-amber-700' },
  success: { Icon: CheckCircle2, dot: 'bg-emerald-600', text: 'text-emerald-700' },
  warning: { Icon: AlertTriangle, dot: 'bg-amber-500', text: 'text-amber-700' },
  error: { Icon: XCircle, dot: 'bg-red-600', text: 'text-red-700' },
  admin: { Icon: UserCog, dot: 'bg-blue-600', text: 'text-blue-700' },
  recovery: { Icon: RefreshCw, dot: 'bg-blue-600', text: 'text-blue-700' },
};

const HAS_TZ = /([+-]\d{2}:?\d{2}|Z)$/;

const formatWhen = (iso) => {
  if (!iso) return '-';
  const d = new Date(HAS_TZ.test(iso) ? iso : `${iso}-03:00`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'medium', timeZone: 'America/Sao_Paulo' });
};

export const OrderTimelineSheet = ({ orderId, onClose }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!orderId) return undefined;
    let active = true;
    setLoading(true);
    setData(null);
    ordersAPI.timeline(orderId)
      .then((r) => { if (active) setData(r.data); })
      .catch(() => { if (active) setData({ error: true }); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [orderId]);

  const order = data?.order;

  return (
    <Sheet open={!!orderId} onOpenChange={(v) => { if (!v) onClose(); }}>
      <SheetContent
        side="right"
        data-testid="order-timeline-sheet"
        className="w-full sm:max-w-lg overflow-y-auto"
      >
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2 text-left">
            <History className="h-5 w-5 text-purple-600" />
            Linha do tempo do pedido
          </SheetTitle>
          <SheetDescription className="text-left">
            Tudo o que aconteceu com este pedido, do mais antigo ao mais recente.
          </SheetDescription>
        </SheetHeader>

        {loading && (
          <div className="flex items-center gap-2 py-10 text-sm text-gray-500" data-testid="timeline-loading">
            <Loader2 className="h-4 w-4 animate-spin" /> Carregando histórico...
          </div>
        )}

        {!loading && data?.error && (
          <p className="py-10 text-sm text-red-600" data-testid="timeline-error">
            Não foi possível carregar a linha do tempo deste pedido.
          </p>
        )}

        {!loading && order && (
          <>
            <div className="mt-4 rounded-xl border bg-gray-50 p-3 text-sm" data-testid="timeline-order-summary">
              <p className="font-semibold text-gray-900">
                {order.kako_id} {order.kako_nickname ? `(${order.kako_nickname})` : ''}
              </p>
              <p className="text-gray-600">
                {order.package_name || 'Pacote'} · R$ {Number(order.amount || 0).toFixed(2)} ·{' '}
                {(order.coins || 0).toLocaleString('pt-BR')} moedas
                {order.bonus_coins ? ` + ${order.bonus_coins.toLocaleString('pt-BR')} bônus` : ''}
              </p>
              <p className="mt-1 text-xs text-gray-500 break-all">
                {order.id} · status atual: <b>{order.status}</b> ·{' '}
                {(order.pix_gateway || order.payment_method || '-').toUpperCase()}
              </p>
            </div>

            {data.reconstructed && (
              <p className="mt-3 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800"
                 data-testid="timeline-reconstructed-notice">
                Pedido anterior a esta funcionalidade: linha do tempo <b>reconstruída</b> a partir
                das datas registradas no pedido.
              </p>
            )}

            <ol className="mt-5 space-y-0" data-testid="timeline-events">
              {(data.events || []).map((ev, i) => {
                const style = KIND[ev.kind] || KIND.created;
                const { Icon } = style;
                const isLast = i === data.events.length - 1;
                return (
                  <li key={`${ev.at}-${i}`} className="relative flex gap-3 pb-5">
                    {!isLast && <span className="absolute left-[11px] top-6 h-full w-px bg-gray-200" />}
                    <span className={`relative z-10 mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full ${style.dot}`}>
                      <Icon className="h-3.5 w-3.5 text-white" />
                    </span>
                    <div className="min-w-0">
                      <p className={`text-sm font-semibold ${style.text}`}>
                        {ev.title}
                        {ev.count > 1 && (
                          <span className="ml-2 rounded-full bg-gray-200 px-2 py-0.5 text-[10px] font-bold text-gray-700">
                            {ev.count}x
                          </span>
                        )}
                      </p>
                      {ev.detail && <p className="text-xs text-gray-600 break-words">{ev.detail}</p>}
                      <p className="mt-0.5 text-[11px] text-gray-400">
                        {formatWhen(ev.at)}
                        {ev.count > 1 && ev.last_at ? ` → ${formatWhen(ev.last_at)}` : ''}
                        {ev.actor ? ` · admin ${ev.actor.slice(0, 8)}` : ''}
                      </p>
                    </div>
                  </li>
                );
              })}
            </ol>

            {(data.events || []).length === 0 && (
              <p className="py-8 text-sm text-gray-500" data-testid="timeline-empty">
                Nenhum evento registrado para este pedido.
              </p>
            )}
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};

export default OrderTimelineSheet;
